class KolWallCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.viewport = this.querySelector('[data-kol-viewport]');
    this.group = this.querySelector('[data-kol-group]');
    this.originalCards = Array.from(this.group?.querySelectorAll('.kol-wall__card') || []);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = Number(this.dataset.speed) || 24;
    this.desktopItems = Math.ceil(Number(this.dataset.desktopItems) || 7);
    this.position = 0;
    this.inView = false;
    this.interacting = false;
    this.frame = null;
    this.pointerId = null;
    this.dragging = false;

    if (!this.viewport || !this.group || this.originalCards.length < 2) return;

    this.hydrateInitialBatch();
    this.imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.hydrateImage(entry.target);
      });
    }, { root: this.viewport, rootMargin: '0px 100% 0px 100%' });
    this.observeImages();
    this.buildBuffer();

    this.onVisibilityChange = () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    };
    this.onMotionChange = () => {
      if (this.reduceMotion.matches) this.stopAutoplay();
      else this.startAutoplay();
    };
    this.onBreakpointChange = () => {
      this.hydrateInitialBatch();
      this.buildBuffer();
    };
    this.onPointerDown = (event) => {
      if (!event.isPrimary || event.pointerType !== 'touch') return;
      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerStartPosition = this.position;
      this.dragging = false;
      this.interacting = true;
      this.stopAutoplay();
    };
    this.onPointerMove = (event) => {
      if (event.pointerId !== this.pointerId) return;
      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;
      if (!this.dragging && Math.abs(deltaY) >= Math.abs(deltaX)) return;
      if (Math.abs(deltaX) > 4) this.dragging = true;
      if (!this.dragging) return;
      event.preventDefault();
      this.position = this.normalizePosition(this.pointerStartPosition - deltaX);
      this.render();
    };
    this.onPointerUp = (event) => {
      if (event.pointerId !== this.pointerId) return;
      this.pointerId = null;
      this.dragging = false;
      this.interacting = false;
      this.startAutoplay();
    };

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.reduceMotion.addEventListener('change', this.onMotionChange);
    this.mobileQuery.addEventListener('change', this.onBreakpointChange);
    this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.viewport.addEventListener('pointermove', this.onPointerMove, { passive: false });
    this.viewport.addEventListener('pointerup', this.onPointerUp, { passive: true });
    this.viewport.addEventListener('pointercancel', this.onPointerUp, { passive: true });

    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      if (this.inView) this.startAutoplay();
      else this.stopAutoplay();
    }, { threshold: 0.05, rootMargin: '200px 0px' });
    this.visibilityObserver.observe(this);

    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.measure(), 100);
    });
    this.resizeObserver.observe(this.viewport);
    this.measure();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    clearTimeout(this.resizeTimer);
    this.resizeObserver?.disconnect();
    this.imageObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.reduceMotion?.removeEventListener('change', this.onMotionChange);
    this.mobileQuery?.removeEventListener('change', this.onBreakpointChange);
    this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
    this.viewport?.removeEventListener('pointermove', this.onPointerMove);
    this.viewport?.removeEventListener('pointerup', this.onPointerUp);
    this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
  }

  hydrateImage(image) {
    if (!image?.dataset.src) return;
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    image.src = image.dataset.src;
    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');
    this.imageObserver?.unobserve(image);
  }

  hydrateInitialBatch() {
    const limit = this.mobileQuery.matches ? 4 : 9;
    this.originalCards.forEach((card, index) => {
      if (index < limit) card.querySelectorAll('[data-kol-image][data-src]').forEach((image) => this.hydrateImage(image));
    });
  }

  observeImages(root = this) {
    root.querySelectorAll('[data-kol-image][data-src]').forEach((image) => this.imageObserver.observe(image));
  }

  buildBuffer() {
    this.group.querySelectorAll('[data-kol-buffer]').forEach((card) => card.remove());
    const bufferCount = Math.min(this.mobileQuery.matches ? 5 : this.desktopItems, this.originalCards.length);
    this.originalCards.slice(0, bufferCount).forEach((card) => {
      const clone = card.cloneNode(true);
      clone.dataset.kolBuffer = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      this.group.append(clone);
      this.observeImages(clone);
    });
    this.measure();
  }

  measure() {
    const firstCard = this.originalCards[0];
    const firstBuffer = this.group.querySelector('[data-kol-buffer]');
    if (!firstCard || !firstBuffer) return;
    const previousWidth = this.loopWidth || 0;
    const progress = previousWidth ? this.position / previousWidth : 0;
    this.loopWidth = firstBuffer.offsetLeft - firstCard.offsetLeft;
    this.position = this.normalizePosition(progress * this.loopWidth);
    this.render();
  }

  normalizePosition(position) {
    if (!this.loopWidth) return Math.max(position, 0);
    return ((position % this.loopWidth) + this.loopWidth) % this.loopWidth;
  }

  render() {
    this.group.style.transform = `translate3d(${-this.position}px, 0, 0)`;
  }

  startAutoplay() {
    if (this.frame || !this.autoplay || !this.inView || this.interacting || document.hidden || this.reduceMotion.matches) return;
    this.lastTime = performance.now();
    const animate = (now) => {
      if (!this.frame) return;
      const elapsed = Math.min(now - this.lastTime, 64);
      this.lastTime = now;
      this.position = this.normalizePosition(this.position + (this.speed * elapsed) / 1000);
      this.render();
      this.frame = requestAnimationFrame(animate);
    };
    this.frame = requestAnimationFrame(animate);
  }

  stopAutoplay() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  refresh() {
    this.originalCards = Array.from(this.group?.querySelectorAll('.kol-wall__card:not([data-kol-buffer])') || []);
    this.hydrateInitialBatch();
    this.buildBuffer();
    this.startAutoplay();
  }
}

if (!customElements.get('kol-wall-carousel')) {
  customElements.define('kol-wall-carousel', KolWallCarousel);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('kol-wall-carousel').forEach((carousel) => carousel.refresh?.());
});
